<template>
  <div>
    <!-- Stage summary tiles (click to filter) -->
    <b-row class="mb-1">
      <b-col
        v-for="tile in tiles"
        :key="tile.key"
        cols="6"
        md
        class="mb-1 mb-md-0"
      >
        <b-card
          no-body
          class="lab-tile mb-0"
          :class="{ 'lab-tile--active': statusFilter === tile.key }"
          role="button"
          @click="toggleStatus(tile.key)"
        >
          <b-card-body class="d-flex align-items-center justify-content-between p-1">
            <div>
              <h2 class="font-weight-bolder mb-0">
                {{ tile.count.toLocaleString() }}
              </h2>
              <small class="text-muted">{{ tile.label }}</small>
            </div>
            <b-avatar
              :variant="`light-${tile.variant}`"
              size="42"
            >
              <feather-icon
                :icon="tile.icon"
                size="20"
              />
            </b-avatar>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-card
      no-body
      class="mb-0"
    >
      <!-- Filters -->
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="3"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Show</label>
            <v-select
              v-model="perPage"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>customers</label>
          </b-col>
          <b-col
            cols="6"
            md="2"
          >
            <label>From (reg. date)</label>
            <b-form-datepicker
              v-model="startDateFilter"
              reset-button
            />
          </b-col>
          <b-col
            cols="6"
            md="2"
          >
            <label>To (reg. date)</label>
            <b-form-datepicker
              v-model="endDateFilter"
              reset-button
            />
          </b-col>
          <b-col
            cols="12"
            md="5"
          >
            <label>Search :</label>
            <div class="d-flex align-items-center">
              <b-form-input
                v-model="searchQuery"
                placeholder="Lab sample ID / job no / customer..."
              />
              <b-button
                variant="flat-secondary"
                class="ml-50 text-nowrap"
                @click="toggleAllJobs"
              >
                <feather-icon :icon="allExpanded ? 'ChevronsUpIcon' : 'ChevronsDownIcon'" />
                {{ allExpanded ? 'Collapse' : 'Expand' }} samples
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Legend -->
      <div class="lab-legend mx-2 mb-1">
        <span
          v-for="col in counterColumns"
          :key="col.key"
          class="lab-legend__item"
        >
          <i :class="`lab-dot lab-dot--${col.key}`" />{{ col.label }}
        </span>
      </div>

      <div
        v-if="isLoading && !groups.length"
        class="text-center text-muted py-3"
      >
        <b-spinner
          small
          class="mr-1"
        />
        Loading...
      </div>
      <div
        v-else-if="!groups.length"
        class="text-center text-muted py-3"
      >
        No lab samples found
      </div>

      <div
        class="lab-groups px-2 pb-1"
        :class="{ 'lab-groups--loading': isLoading }"
      >
        <!-- Customer -->
        <section
          v-for="(group, gIndex) in groups"
          :key="group.Customer"
          class="lab-group"
        >
          <header
            class="lab-group__head"
            role="button"
            @click="toggleGroup(group.Customer)"
          >
            <feather-icon
              :icon="isGroupOpen(group.Customer) ? 'ChevronDownIcon' : 'ChevronRightIcon'"
              size="16"
              class="text-muted"
            />
            <span class="lab-group__no">{{ rowOffset + gIndex + 1 }}</span>
            <div class="lab-group__name">
              <strong>{{ group.Customer }}</strong>
              <small class="text-muted">
                {{ group.Jobs.length }} job(s) · {{ group.Nos }} sample(s)
              </small>
            </div>
            <div class="lab-group__progress">
              <b-progress
                :value="group.Progress"
                :variant="progressVariant(group.Progress)"
                height="8px"
              />
              <span class="font-weight-bold">{{ group.Progress }}%</span>
            </div>
          </header>

          <div v-show="isGroupOpen(group.Customer)">
            <!-- Column header -->
            <div class="lab-row lab-row--head">
              <div>Job</div>
              <div
                v-for="col in counterColumns"
                :key="col.key"
                class="text-center"
              >
                {{ col.short }}
              </div>
              <div>Progress</div>
            </div>

            <!-- Job -->
            <div
              v-for="job in group.Jobs"
              :key="job.JobRegisterID"
              class="lab-job"
            >
              <div
                class="lab-row lab-row--job"
                role="button"
                @click="toggleJob(job.JobRegisterID)"
              >
                <div class="lab-job__info">
                  <feather-icon
                    :icon="isJobOpen(job.JobRegisterID) ? 'MinusSquareIcon' : 'PlusSquareIcon'"
                    size="15"
                    class="text-muted"
                  />
                  <div>
                    <span class="lab-job__no">{{ job.JobNo }}</span>
                    <b-badge
                      v-if="job.RegType"
                      variant="light-primary"
                      class="ml-50"
                    >
                      {{ job.RegType }}
                    </b-badge>
                    <div>
                      <small class="text-muted">{{ dateTime(job.RegTime || job.RegDate) }}</small>
                      <small
                        v-if="job.DueDate"
                        :class="isOverdue(job) ? 'text-danger font-weight-bold' : 'text-muted'"
                      >
                        · Due {{ dateSimple(job.DueDate) }}<template v-if="isOverdue(job)"> (overdue)</template>
                      </small>
                    </div>
                  </div>
                </div>
                <div
                  v-for="col in counterColumns"
                  :key="col.key"
                  class="text-center"
                >
                  <span
                    class="lab-chip"
                    :class="[`lab-chip--${col.key}`, { 'lab-chip--zero': !job[col.field] }]"
                  >{{ job[col.field] }}</span>
                </div>
                <div class="lab-job__progress">
                  <b-progress
                    :value="job.Progress"
                    :variant="progressVariant(job.Progress)"
                    height="8px"
                  />
                  <small class="font-weight-bold">{{ job.Progress }}%</small>
                </div>
              </div>

              <!-- Samples -->
              <div
                v-show="isJobOpen(job.JobRegisterID)"
                class="lab-samples"
              >
                <div
                  v-for="sample in job.Samples"
                  :key="sample.LabSampleID"
                  class="lab-sample"
                >
                  <span class="lab-sample__seq">{{ sample.Sequence }}.</span>
                  <span class="lab-sample__id">{{ sample.LabSampleID }}</span>
                  <b-badge :variant="stageMeta(sample.Stage).badge">
                    {{ stageMeta(sample.Stage).label }}
                  </b-badge>
                  <div class="lab-steps">
                    <span
                      v-for="step in steps"
                      :key="step.key"
                      :class="{ 'lab-steps__done': sample[step.key] === 1 }"
                      :title="stepTitle(sample, step)"
                    >
                      <feather-icon
                        :icon="sample[step.key] === 1 ? 'CheckCircleIcon' : 'CircleIcon'"
                        size="12"
                      />
                      {{ step.label }}
                      <small
                        v-if="sample[step.date]"
                        class="lab-steps__meta"
                      >{{ dateShort(sample[step.date]) }}<template v-if="sample[step.user]"> · {{ sample[step.user] }}</template></small>
                    </span>
                  </div>
                  <div class="lab-sample__progress">
                    <b-progress
                      :value="sample.Progress"
                      :variant="stageMeta(sample.Stage).variant"
                      height="6px"
                    />
                    <small class="font-weight-bold">{{ sample.Progress }}%</small>
                  </div>
                  <small class="text-muted lab-sample__pack">
                    {{ sample.Bags }} bag(s) · {{ formatWeight(sample.GrossWeight) }}<template v-if="sample.PackCondition"> · {{ sample.PackCondition }}</template>
                  </small>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ total.toLocaleString() }} customers</span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="total"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BFormInput, BFormDatepicker, BPagination, BButton,
  BBadge, BProgress, BAvatar, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from 'axios'
import moment from 'moment'
import { useToast } from 'vue-toastification/composition'
import { getToken } from '@/auth/utils'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const STAGES = {
  registered: {
    label: 'Registered', badge: 'light-secondary', variant: 'secondary',
  },
  preparation: {
    label: 'Preparation', badge: 'light-info', variant: 'info',
  },
  picking: {
    label: 'Analyze pick', badge: 'light-warning', variant: 'warning',
  },
  working: {
    label: 'Analyze work', badge: 'light-primary', variant: 'primary',
  },
  reporting: {
    label: 'Reporting', badge: 'light-success', variant: 'success',
  },
}

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BFormDatepicker,
    BPagination,
    BButton,
    BBadge,
    BProgress,
    BAvatar,
    BSpinner,
    vSelect,
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  data() {
    return {
      groups: [],
      total: 0,
      summary: {
        registered: 0, preparation: 0, picking: 0, working: 0, reporting: 0,
      },
      isLoading: false,
      perPage: 10,
      perPageOptions: [5, 10, 20, 50],
      currentPage: 1,
      searchQuery: '',
      statusFilter: '',
      startDateFilter: '',
      endDateFilter: '',
      searchTimer: null,
      requestId: 0,
      closedGroups: {},
      openJobs: {},
      steps: [
        {
          key: 'Preparation', label: 'Prep', date: 'PrepDate', user: 'PrepUser',
        },
        {
          key: 'Picking', label: 'Pick', date: 'PickDate', user: 'PickUser',
        },
        {
          key: 'Working', label: 'Work', date: 'WorkDate', user: 'WorkUser',
        },
        {
          key: 'Reporting', label: 'Report', date: 'ReportingDate', user: 'ReportingUser',
        },
      ],
      // One column per lab stage, same order as the pipeline.
      counterColumns: [
        {
          key: 'nos', field: 'Nos', short: 'NOS', label: 'NOS · number of samples',
        },
        {
          key: 'registered', field: 'Registered', short: 'Reg', label: 'Reg · registered, not started',
        },
        {
          key: 'preparation', field: 'Preparation', short: 'Prep', label: 'Prep · preparation done',
        },
        {
          key: 'picking', field: 'Picking', short: 'Pick', label: 'Pick · analyze-pick done',
        },
        {
          key: 'working', field: 'Working', short: 'Work', label: 'Work · analyze-work done',
        },
        {
          key: 'reporting', field: 'Reporting', short: 'Report', label: 'Report · reporting done',
        },
        {
          key: 'outstanding', field: 'Outstanding', short: 'Outstd', label: 'Outstd · report not yet done',
        },
      ],
    }
  },
  computed: {
    tiles() {
      const all = Object.values(this.summary).reduce((a, b) => a + b, 0)
      return [
        {
          key: '', label: 'NOS · all samples', count: all, variant: 'primary', icon: 'LayersIcon',
        },
        {
          key: 'registered', label: 'Registered', count: this.summary.registered, variant: 'secondary', icon: 'InboxIcon',
        },
        {
          key: 'preparation', label: 'Preparation', count: this.summary.preparation, variant: 'info', icon: 'PackageIcon',
        },
        {
          key: 'picking', label: 'Analyze pick', count: this.summary.picking, variant: 'warning', icon: 'CrosshairIcon',
        },
        {
          key: 'working', label: 'Analyze work', count: this.summary.working, variant: 'primary', icon: 'ToolIcon',
        },
        {
          key: 'reporting', label: 'Reporting', count: this.summary.reporting, variant: 'success', icon: 'FileTextIcon',
        },
      ]
    },
    rowOffset() {
      return (this.currentPage - 1) * this.perPage
    },
    dataMeta() {
      const from = this.total ? this.rowOffset + 1 : 0
      return { from, to: Math.min(this.total, this.currentPage * this.perPage) }
    },
    allJobIds() {
      return this.groups.flatMap(group => group.Jobs.map(job => job.JobRegisterID))
    },
    allExpanded() {
      return this.allJobIds.length > 0 && this.allJobIds.every(id => this.openJobs[id])
    },
  },
  watch: {
    perPage() { this.resetAndFetch() },
    startDateFilter() { this.resetAndFetch() },
    endDateFilter() { this.resetAndFetch() },
    currentPage() { this.fetchProgress() },
    searchQuery() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.resetAndFetch(), 400)
    },
  },
  mounted() {
    this.fetchProgress()
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
  },
  methods: {
    stageMeta(stage) {
      return STAGES[stage] || STAGES.registered
    },
    progressVariant(value) {
      if (value >= 100) return 'success'
      if (value >= 67) return 'primary'
      if (value >= 34) return 'warning'
      return 'secondary'
    },
    dateSimple(value) {
      return value ? moment(String(value)).format('DD MMM YYYY') : '-'
    },
    dateShort(value) {
      return value ? moment(String(value)).format('DD/MM HH:mm') : ''
    },
    dateTime(value) {
      return value ? moment(String(value)).format('DD/MM/YY HH:mm') : '-'
    },
    stepTitle(sample, step) {
      if (!sample[step.date]) return sample[step.key] === 1 ? `${step.label}: done` : `${step.label}: pending`
      const user = sample[step.user] ? ` by ${sample[step.user]}` : ''
      return `${step.label}: ${this.dateTime(sample[step.date])}${user}`
    },
    isOverdue(job) {
      return Boolean(job.DueDate) && job.Outstanding > 0 && moment(String(job.DueDate)).isBefore(moment(), 'day')
    },
    formatWeight(value) {
      return value === null || value === undefined ? '-' : `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 4 })} kg`
    },
    isGroupOpen(customer) {
      return !this.closedGroups[customer]
    },
    toggleGroup(customer) {
      this.$set(this.closedGroups, customer, this.isGroupOpen(customer))
    },
    isJobOpen(id) {
      return Boolean(this.openJobs[id])
    },
    toggleJob(id) {
      this.$set(this.openJobs, id, !this.openJobs[id])
    },
    toggleAllJobs() {
      const open = !this.allExpanded
      this.allJobIds.forEach(id => this.$set(this.openJobs, id, open))
    },
    toggleStatus(key) {
      this.statusFilter = this.statusFilter === key ? '' : key
      this.resetAndFetch()
    },
    resetAndFetch() {
      if (this.currentPage !== 1) {
        this.currentPage = 1 // the currentPage watcher fetches
      } else {
        this.fetchProgress()
      }
    },
    fetchProgress() {
      const requestId = this.requestId + 1
      this.requestId = requestId
      this.isLoading = true
      axios
        .get('https://api.geolims.com/invoice_service/getprogressstatus', {
          headers: { 'X-Token-Access': `Bearer ${getToken()}` },
          params: {
            page: this.currentPage,
            perPage: this.perPage,
            search: this.searchQuery || undefined,
            status: this.statusFilter || undefined,
            startDate: this.startDateFilter || undefined,
            endDate: this.endDateFilter || undefined,
          },
        })
        .then(response => {
          if (requestId !== this.requestId) return // a newer request superseded this one
          this.groups = response.data.Payload || []
          this.total = response.data.Total || 0
          if (response.data.Summary) this.summary = response.data.Summary
        })
        .catch(() => {
          if (requestId !== this.requestId) return
          this.toast({
            component: ToastificationContent,
            props: { title: 'Error fetching progress status', icon: 'AlertTriangleIcon', variant: 'danger' },
          })
        })
        .finally(() => {
          if (requestId === this.requestId) this.isLoading = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
$c-nos: #ffd43b;
$c-registered: #e879f9;
$c-preparation: #ffb98a;
$c-picking: #ff9f43;
$c-working: #4cc9f0;
$c-reporting: #9f6bf2;
$c-outstanding: #ea5455;
$line: rgba(130, 134, 139, 0.22);
$soft: rgba(130, 134, 139, 0.07);

.per-page-selector {
  width: 90px;
}

.lab-tile {
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &--active {
    border-color: #7367f0;
    box-shadow: 0 4px 18px rgba(115, 103, 240, 0.35);
  }
}

.lab-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  font-size: 0.75rem;
  color: #82868b;

  &__item {
    display: inline-flex;
    align-items: center;
  }
}

.lab-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  margin-right: 6px;
  border-radius: 50%;

  &--nos { background: $c-nos; }
  &--registered { background: $c-registered; }
  &--preparation { background: $c-preparation; }
  &--picking { background: $c-picking; }
  &--working { background: $c-working; }
  &--reporting { background: $c-reporting; }
  &--outstanding { background: $c-outstanding; }
}

.lab-groups--loading {
  opacity: 0.55;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

.lab-group {
  margin-bottom: 14px;
  border: 1px solid $line;
  border-radius: 10px;
  overflow: hidden;

  &__head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: $soft;
    border-bottom: 1px solid $line;
  }

  &__no {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 26px;
    height: 26px;
    padding: 0 6px;
    border-radius: 13px;
    background: rgba(115, 103, 240, 0.14);
    color: #7367f0;
    font-size: 0.8rem;
    font-weight: 700;
  }

  &__name {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;

    strong {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__progress {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 220px;

    .progress {
      flex: 1;
    }
  }
}

// Shared grid: job | 7 counters | progress
.lab-row {
  display: grid;
  grid-template-columns: minmax(210px, 1.6fr) repeat(7, minmax(48px, 60px)) minmax(130px, 1fr);
  align-items: center;
  gap: 8px;
  padding: 8px 14px;

  &--head {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #82868b;
    border-bottom: 1px solid $line;
  }

  &--job {
    cursor: pointer;
    transition: background 0.12s ease;

    &:hover {
      background: $soft;
    }
  }
}

.lab-job {
  border-bottom: 1px solid $line;

  &:last-child {
    border-bottom: 0;
  }

  &__info {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }

  &__no {
    font-weight: 700;
  }

  &__progress {
    display: flex;
    align-items: center;
    gap: 8px;

    .progress {
      flex: 1;
    }
  }
}

.lab-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 38px;
  height: 26px;
  padding: 0 8px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #2f2b3d;

  &--nos { background: $c-nos; }
  &--registered { background: $c-registered; color: #fff; }
  &--preparation { background: $c-preparation; }
  &--picking { background: $c-picking; color: #fff; }
  &--working { background: $c-working; }
  &--reporting { background: $c-reporting; color: #fff; }
  &--outstanding { background: $c-outstanding; color: #fff; }

  // A zero is quiet so the non-zero counters stand out.
  &--zero {
    background: $soft;
    color: #b9b9c3;
  }
}

.lab-samples {
  margin: 0 14px 10px 36px;
  border-left: 2px solid $line;
}

.lab-sample {
  display: grid;
  grid-template-columns: 28px minmax(110px, 150px) 100px minmax(330px, 1.4fr) minmax(120px, 180px) minmax(120px, 1fr);
  align-items: center;
  gap: 10px;
  padding: 6px 12px;

  &:nth-child(odd) {
    background: $soft;
  }

  &__seq {
    color: #82868b;
    font-weight: 600;
    text-align: right;
  }

  &__id {
    font-weight: 600;
  }

  &__progress {
    display: flex;
    align-items: center;
    gap: 8px;

    .progress {
      flex: 1;
    }
  }

  &__pack {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.lab-steps {
  display: flex;
  gap: 16px;
  font-size: 0.75rem;
  color: #b9b9c3;

  &__done {
    color: #28c76f;
  }

  > span {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.2;
  }

  &__meta {
    color: #82868b;
    font-size: 0.68rem;
    white-space: nowrap;
  }
}

@media (max-width: 991px) {
  .lab-group__progress {
    width: 120px;
  }

  .lab-group > div {
    overflow-x: auto;
  }

  .lab-row,
  .lab-sample {
    min-width: 980px;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
